import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { gsap } from 'gsap';
import { Link } from 'react-router-dom';
import { FiSend, FiCheckCircle, FiX, FiMail } from 'react-icons/fi';
import { ThemeContext } from '../context/ThemeContext';
import { AuthContext } from '../context/AuthContext';
import { FormAlert } from './form';
import { SUPPORT_EMAIL } from '../config/emailjs';
import { apiUrl } from '../config';
import { readApiJson } from '../utils/apiResponse';

const SUBJECTS = [
  { value: 'Consulta general', label: 'Consulta general' },
  { value: 'Planes', label: 'Planes' },
  { value: 'Soporte', label: 'Soporte' },
  { value: 'Cómo funciona', label: 'Cómo funciona' },
  { value: 'Otro', label: 'Otro' },
];

const EMPTY_FORM = {
  name: '',
  email: '',
  company: '',
  subject: '',
  message: '',
};

const emailOk = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const lockScroll = () => {
  document.body.style.overflow = 'hidden';
  document.body.classList.add('modal-open');
  const main = document.getElementById('main-scroll');
  if (main) {
    main.dataset.prevOverflow = main.style.overflow;
    main.style.overflow = 'hidden';
  }
};

const unlockScroll = () => {
  document.body.style.overflow = '';
  document.body.classList.remove('modal-open');
  const main = document.getElementById('main-scroll');
  if (main) {
    main.style.overflow = main.dataset.prevOverflow || '';
    delete main.dataset.prevOverflow;
  }
};

const ContactModal = ({ isOpen, onClose, preSelectedPlan = null }) => {
  const { modoOscuro } = useContext(ThemeContext);
  const { user } = useContext(AuthContext);
  const overlayRef = useRef(null);
  const modalRef = useRef(null);
  const contentRef = useRef(null);
  const nameInputRef = useRef(null);
  const [rendered, setRendered] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState(EMPTY_FORM);

  const resetForm = useCallback(() => {
    const planMatch = SUBJECTS.find((s) => s.value === preSelectedPlan || s.label === preSelectedPlan);
    setForm({
      ...EMPTY_FORM,
      name: user?.name || '',
      email: user?.email || '',
      subject: planMatch?.value || (preSelectedPlan && String(preSelectedPlan).startsWith('Plan') ? 'Planes' : ''),
    });
    setErrors({});
    setSubmitError('');
    setIsSubmitting(false);
    setIsSuccess(false);
  }, [preSelectedPlan, user?.name, user?.email]);

  useEffect(() => {
    if (isOpen) {
      setRendered(true);
      resetForm();
      lockScroll();
      return undefined;
    }

    if (!rendered) return undefined;

    const tl = gsap.timeline({
      onComplete: () => {
        unlockScroll();
        setRendered(false);
      },
    });
    if (modalRef.current) {
      tl.to(modalRef.current, { x: '100%', duration: 0.35, ease: 'power3.in' });
    }
    if (overlayRef.current) {
      tl.to(overlayRef.current, { opacity: 0, duration: 0.25, ease: 'power2.in' }, '-=0.15');
    }
    if (!modalRef.current && !overlayRef.current) {
      unlockScroll();
      setRendered(false);
    }
    return () => tl.kill();
  }, [isOpen, rendered, resetForm]);

  useEffect(() => {
    if (!rendered || !isOpen) return undefined;
    const tl = gsap.timeline();
    tl.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: 'power2.out' });
    tl.fromTo(modalRef.current, { x: '100%' }, { x: '0%', duration: 0.4, ease: 'power3.out' }, '-=0.1');
    tl.fromTo(contentRef.current, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }, '-=0.15');
    const focusId = window.setTimeout(() => nameInputRef.current?.focus(), 420);
    return () => {
      tl.kill();
      window.clearTimeout(focusId);
    };
  }, [rendered, isOpen]);

  useEffect(() => {
    if (!rendered) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rendered, isSubmitting, onClose]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim() || form.name.trim().length < 2) next.name = 'Ingresá tu nombre.';
    if (!emailOk(form.email.trim())) next.email = 'Ingresá un email válido.';
    if (!form.subject) next.subject = 'Elegí un asunto.';
    if (!form.message.trim() || form.message.trim().length < 10) next.message = 'Escribí al menos 10 caracteres.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || '—',
        subject: form.subject,
        message: form.message.trim(),
      };

      const res = await fetch(apiUrl('/api/contact'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: payload.name,
          email: payload.email,
          company: form.company.trim(),
          subject: payload.subject,
          message: payload.message,
        }),
      });
      const data = await readApiJson(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo enviar');
      setIsSuccess(true);
    } catch (error) {
      console.error('Error al enviar formulario:', error);
      setSubmitError(`No se pudo enviar. Escribinos a ${SUPPORT_EMAIL}.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!rendered) return null;

  const panel = modoOscuro ? 'bg-black text-white border-gray-800' : 'bg-white text-gray-900 border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const divide = modoOscuro ? 'border-gray-800' : 'border-gray-200';
  const inputClass = `w-full px-3.5 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-[#1F80FF]/40 ${
    modoOscuro
      ? 'bg-gray-900 border-gray-700 text-white placeholder:text-gray-500'
      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'
  }`;
  const inputError = modoOscuro ? 'border-red-500/70' : 'border-red-400';
  const labelClass = `block mb-1.5 text-xs font-medium ${textMuted}`;

  return createPortal(
    <div className="fixed inset-0 z-[99999]" role="dialog" aria-modal="true" aria-labelledby="contact-title">
      <div
        ref={overlayRef}
        className="fixed inset-0 bg-black/70 backdrop-blur-md"
        onClick={() => !isSubmitting && onClose()}
      />

      <div
        ref={modalRef}
        className={`fixed top-0 right-0 h-full w-full sm:max-w-md border-l shadow-2xl flex flex-col ${panel}`}
      >
        <div className={`flex items-center justify-between px-5 py-3.5 border-b shrink-0 ${divide}`}>
          <Link to="/" onClick={onClose} className="hover:opacity-80 transition-opacity">
            <img
              src={modoOscuro ? '/logowhite.png' : '/logoblack.png'}
              alt="Contsuite"
              className="h-6 w-auto"
            />
          </Link>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className={`p-2 rounded-xl transition-colors ${
              modoOscuro ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            }`}
            aria-label="Cerrar"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div ref={contentRef} className="flex-1 overflow-y-auto overscroll-contain px-5 py-6">
          {isSuccess ? (
            <div className="flex flex-col items-center justify-center min-h-full text-center py-10">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${modoOscuro ? 'bg-green-500/15' : 'bg-green-50'}`}>
                <FiCheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${textPri}`}>Mensaje enviado</h3>
              <p className={`text-sm max-w-xs mb-6 ${textSec}`}>
                Te respondemos a {SUPPORT_EMAIL}. En general, en 24 h hábiles.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-[#1F80FF] hover:bg-[#004AAD] text-white transition-colors"
              >
                Cerrar
              </button>
            </div>
          ) : (
            <>
              <p className={`text-[11px] font-semibold tracking-[0.18em] uppercase mb-2 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                Contacto
              </p>
              <h2 id="contact-title" className={`text-2xl font-bold leading-tight mb-2 ${textPri}`}>
                Escribinos
              </h2>
              <p className={`text-sm leading-relaxed mb-6 ${textSec}`}>
                Consultas de producto, planes o soporte. Te respondemos a{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium underline underline-offset-2">
                  {SUPPORT_EMAIL}
                </a>
                {' '}(24 h hábiles).
              </p>

              <FormAlert type="error">{submitError}</FormAlert>

              <form onSubmit={handleSubmit} className="space-y-3.5" noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <label htmlFor="contact-name" className={labelClass}>Nombre *</label>
                    <input
                      ref={nameInputRef}
                      id="contact-name"
                      type="text"
                      autoComplete="name"
                      value={form.name}
                      onChange={(e) => setField('name', e.target.value)}
                      className={`${inputClass} ${errors.name ? inputError : ''}`}
                      placeholder="Tu nombre"
                    />
                    {errors.name && <p className="text-[11px] text-red-500 mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="contact-email" className={labelClass}>Email *</label>
                    <input
                      id="contact-email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={(e) => setField('email', e.target.value)}
                      className={`${inputClass} ${errors.email ? inputError : ''}`}
                      placeholder="hola@tuempresa.com"
                    />
                    {errors.email && <p className="text-[11px] text-red-500 mt-1">{errors.email}</p>}
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-company" className={labelClass}>Empresa</label>
                  <input
                    id="contact-company"
                    type="text"
                    autoComplete="organization"
                    value={form.company}
                    onChange={(e) => setField('company', e.target.value)}
                    className={inputClass}
                    placeholder="Opcional"
                  />
                </div>

                <div>
                  <label htmlFor="contact-subject" className={labelClass}>Asunto *</label>
                  <select
                    id="contact-subject"
                    value={form.subject}
                    onChange={(e) => setField('subject', e.target.value)}
                    className={`${inputClass} ${errors.subject ? inputError : ''}`}
                  >
                    <option value="">Elegí un asunto</option>
                    {SUBJECTS.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                  {errors.subject && <p className="text-[11px] text-red-500 mt-1">{errors.subject}</p>}
                </div>

                <div>
                  <label htmlFor="contact-message" className={labelClass}>Mensaje *</label>
                  <textarea
                    id="contact-message"
                    rows={5}
                    value={form.message}
                    onChange={(e) => setField('message', e.target.value)}
                    className={`${inputClass} resize-none min-h-[120px] ${errors.message ? inputError : ''}`}
                    placeholder="¿En qué te podemos ayudar?"
                  />
                  {errors.message && <p className="text-[11px] text-red-500 mt-1">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full mt-1 bg-[#1F80FF] hover:bg-[#004AAD] text-white py-3 px-4 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Enviando…
                    </>
                  ) : (
                    <>
                      <FiSend className="w-4 h-4" />
                      Enviar
                    </>
                  )}
                </button>
              </form>

              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className={`mt-5 inline-flex items-center gap-1.5 text-xs ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}
              >
                <FiMail className="w-3.5 h-3.5" />
                {SUPPORT_EMAIL}
              </a>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ContactModal;
